import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { THEME } from '../../utils/theme';
import api from '../../utils/api';
import Spinner from '../ui/Spinner';
import Avatar from '../ui/Avatar';
import Badge from '../ui/Badge';

const SquadManagementModal = ({ squads, currentSquad, currentUser, onClose, onSquadsChange, onSquadSelect }) => {
  const [activeTab, setActiveTab] = useState('my-squads');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // Create squad form
  const [newSquadName, setNewSquadName] = useState('');
  const [newSquadIcon, setNewSquadIcon] = useState('🎮');
  const [creating, setCreating] = useState(false);
  // Join squad form
  const [inviteCode, setInviteCode] = useState('');
  const [joining, setJoining] = useState(false);
  // Edit squad form
  const [editingSquad, setEditingSquad] = useState(null);
  const [editName, setEditName] = useState('');
  const [editIcon, setEditIcon] = useState('');
  const [saving, setSaving] = useState(false);
  // Squad details
  const [squadDetails, setSquadDetails] = useState({});
  // Kick voting
  const [kickVotes, setKickVotes] = useState({});  // { squadId: { targetUserId: { votes: [], voted: boolean } } }
  const [kickingUser, setKickingUser] = useState(null);
  // Discord settings
  const [discordSettingsSquad, setDiscordSettingsSquad] = useState(null);
  const [discordGuilds, setDiscordGuilds] = useState([]);
  const [discordChannels, setDiscordChannels] = useState({ text_channels: [], voice_channels: [], categories: [] });
  const [discordConfig, setDiscordConfig] = useState({
    server_id: '',
    channel_id: '',
    voice_category_id: '',
    voice_channel_id: '',
    sync_activity: false,
    sync_chat: false,
    widget_enabled: false
  });
  const [loadingDiscord, setLoadingDiscord] = useState(false);
  const [savingDiscord, setSavingDiscord] = useState(false);

  const myColor = currentUser?.theme_color || THEME.success;
  const SQUAD_ICONS = ['🎮', '👾', '🕹️', '🎯', '🏆', '⚔️', '🛡️', '🚀', '🌟', '💫', '🔥', '❄️', '🌈', '🎪', '🎭', '🎨'];

  const loadKickVotes = async (squadId) => {
    try {
      const votes = await api.get(`/api/squads/${squadId}/kick-votes`);
      // Transform to { targetUserId: { votes: [...], voted: boolean } }
      const votesByTarget = {};
      votes.forEach(vote => {
        if (!votesByTarget[vote.target_user_id]) {
          votesByTarget[vote.target_user_id] = { votes: [], voted: false };
        }
        votesByTarget[vote.target_user_id].votes.push(vote);
        if (vote.voted_by === currentUser?.id) {
          votesByTarget[vote.target_user_id].voted = true;
        }
      });
      setKickVotes(prev => ({ ...prev, [squadId]: votesByTarget }));
    } catch (err) { console.error('Failed to load kick votes:', err); }
  };

  useEffect(() => {
    // Load details for each squad
    squads.forEach(async (squad) => {
      try {
        const details = await api.get(`/api/squads/${squad.id}`);
        setSquadDetails(prev => ({ ...prev, [squad.id]: details }));
        // Load kick votes for squads with 3+ members
        if (details.members?.length > 2) {
          loadKickVotes(squad.id);
        }
      } catch (err) { console.error('Failed to load squad details:', err); }
    });
  }, [squads]);

  const handleCreateSquad = async () => {
    if (!newSquadName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const newSquad = await api.post('/api/squads', { name: newSquadName.trim(), icon: newSquadIcon });
      onSquadsChange([...squads, newSquad]);
      onSquadSelect(newSquad);
      setNewSquadName('');
      setNewSquadIcon('🎮');
      setActiveTab('my-squads');
    } catch (err) { setError(err.message); }
    finally { setCreating(false); }
  };

  const handleJoinSquad = async () => {
    if (!inviteCode.trim()) return;
    setJoining(true);
    setError(null);
    try {
      const result = await api.post('/api/squads/join', { invite_code: inviteCode.trim() });
      onSquadsChange([...squads, result.squad]);
      onSquadSelect(result.squad);
      setInviteCode('');
      setActiveTab('my-squads');
    } catch (err) { setError(err.message); }
    finally { setJoining(false); }
  };

  const handleUpdateSquad = async () => {
    if (!editingSquad || !editName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await api.put(`/api/squads/${editingSquad.id}`, { name: editName.trim(), icon: editIcon });
      onSquadsChange(squads.map(s => s.id === editingSquad.id ? { ...s, ...updated } : s));
      if (currentSquad?.id === editingSquad.id) onSquadSelect({ ...currentSquad, ...updated });
      setEditingSquad(null);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleLeaveSquad = async (squad) => {
    if (!confirm(`Leave "${squad.name}"? You'll need an invite code to rejoin.`)) return;
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/api/squads/${squad.id}/leave`);
      const remaining = squads.filter(s => s.id !== squad.id);
      onSquadsChange(remaining);
      if (currentSquad?.id === squad.id && remaining.length > 0) onSquadSelect(remaining[0]);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleDisbandSquad = async (squad) => {
    if (!confirm(`Disband "${squad.name}"? This will remove all members and delete the squad permanently.`)) return;
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/api/squads/${squad.id}/disband`);
      const remaining = squads.filter(s => s.id !== squad.id);
      onSquadsChange(remaining);
      if (currentSquad?.id === squad.id && remaining.length > 0) onSquadSelect(remaining[0]);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleVoteToKick = async (squadId, userId) => {
    setKickingUser(userId);
    setError(null);
    setSuccess(null);
    try {
      const result = await api.post(`/api/squads/${squadId}/kick/${userId}`);
      setSuccess(result.message);
      // Reload kick votes and squad details
      loadKickVotes(squadId);
      const details = await api.get(`/api/squads/${squadId}`);
      setSquadDetails(prev => ({ ...prev, [squadId]: details }));
      // If user was kicked (message contains "Kicked"), might need to update squads list
      if (result.message.includes('Kicked')) {
        const userSquads = await api.get('/api/squads');
        onSquadsChange(userSquads);
      }
    } catch (err) { setError(err.message); }
    finally { setKickingUser(null); }
  };

  const handleRemoveKickVote = async (squadId, userId) => {
    setKickingUser(userId);
    setError(null);
    setSuccess(null);
    try {
      await api.delete(`/api/squads/${squadId}/kick/${userId}`);
      setSuccess('Vote removed');
      loadKickVotes(squadId);
    } catch (err) { setError(err.message); }
    finally { setKickingUser(null); }
  };

  const copyInviteCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      alert('Invite code copied!');
    } catch { alert(`Invite code: ${code}`); }
  };

  // Discord settings functions
  const openDiscordSettings = async (squad) => {
    setDiscordSettingsSquad(squad);
    setLoadingDiscord(true);
    setError(null);
    try {
      // Load current squad Discord config
      const config = await api.get(`/api/integrations/discord/squad/${squad.id}`);
      setDiscordConfig({
        server_id: config.server_id || '',
        channel_id: config.channel_id || '',
        voice_category_id: config.voice_category_id || '',
        voice_channel_id: config.voice_channel_id || '',
        sync_activity: config.sync_activity || false,
        sync_chat: config.sync_chat || false,
        widget_enabled: config.widget_enabled || false
      });
      // Load available Discord servers
      const guilds = await api.get('/api/integrations/discord/guilds');
      setDiscordGuilds(guilds.guilds || []);
      // Load channels if server already selected
      if (config.server_id) {
        try {
          const channels = await api.get(`/api/integrations/discord/guilds/${config.server_id}/channels`);
          setDiscordChannels({
            text_channels: channels.text_channels || [],
            voice_channels: channels.voice_channels || [],
            categories: channels.categories || []
          });
          setBotNeedsInvite(false);
        } catch (channelErr) {
          const is404 = channelErr.status === 404 || channelErr.response?.status === 404 || channelErr.message?.includes('not found');
          if (is404) {
            setBotNeedsInvite(true);
            setDiscordChannels({ text_channels: [], voice_channels: [], categories: [] });
            try {
              const invite = await api.get('/api/integrations/discord/bot-invite-url');
              setBotInviteUrl(invite.url);
            } catch (e) {}
          }
        }
      }
    } catch (err) {
      if (err.status === 401 || err.response?.status === 401 || err.message?.includes('Discord') || err.message?.includes('Unauthorized')) {
        setError('You need to link your Discord account first (Settings → Integrations → Discord)');
      } else {
        setError(err.message || 'Failed to load Discord settings');
      }
    } finally {
      setLoadingDiscord(false);
    }
  };

  const [botNeedsInvite, setBotNeedsInvite] = useState(false);
  const [botInviteUrl, setBotInviteUrl] = useState(null);

  const handleSelectDiscordServer = async (serverId) => {
    setDiscordConfig(prev => ({ ...prev, server_id: serverId, channel_id: '', voice_category_id: '', voice_channel_id: '' }));
    setBotNeedsInvite(false);
    if (!serverId) {
      setDiscordChannels({ text_channels: [], voice_channels: [], categories: [] });
      return;
    }
    try {
      const channels = await api.get(`/api/integrations/discord/guilds/${serverId}/channels`);
      setDiscordChannels({
        text_channels: channels.text_channels || [],
        voice_channels: channels.voice_channels || [],
        categories: channels.categories || []
      });
      setBotNeedsInvite(false);
    } catch (err) {
      // If 404, the bot isn't in this server - show invite button
      console.log('Channel fetch error:', err, 'status:', err.status, 'response:', err.response);
      const is404 = err.status === 404 || err.response?.status === 404 || err.message?.includes('not found') || err.message?.includes('bot not in server');
      console.log('Is 404?', is404);
      if (is404) {
        console.log('Setting botNeedsInvite to true');
        setBotNeedsInvite(true);
        setDiscordChannels({ text_channels: [], voice_channels: [], categories: [] });
        // Get the bot invite URL
        try {
          const invite = await api.get('/api/integrations/discord/bot-invite-url');
          setBotInviteUrl(invite.url);
        } catch (e) { console.error('Failed to get bot invite URL:', e); }
      } else {
        setError('Failed to load channels: ' + (err.message || 'Unknown error'));
        setDiscordChannels({ text_channels: [], voice_channels: [], categories: [] });
      }
    }
  };

  const handleInviteBot = async () => {
    if (!botInviteUrl) {
      try {
        const invite = await api.get('/api/integrations/discord/bot-invite-url');
        if (invite.url) window.open(invite.url, '_blank');
      } catch (e) { setError('Could not get bot invite URL'); }
    } else {
      window.open(botInviteUrl, '_blank');
    }
  };

  const handleSaveDiscordSettings = async () => {
    if (!discordSettingsSquad) return;
    setSavingDiscord(true);
    setError(null);
    try {
      await api.put(`/api/integrations/discord/squad/${discordSettingsSquad.id}/link`, {
        server_id: discordConfig.server_id,
        channel_id: discordConfig.channel_id || null,
        voice_category_id: discordConfig.voice_category_id || null,
        voice_channel_id: discordConfig.voice_channel_id || null,
        sync_activity: discordConfig.sync_activity,
        sync_chat: discordConfig.sync_chat,
        widget_enabled: discordConfig.widget_enabled
      });
      setSuccess('Discord settings saved!');
      // Update squad details
      const details = await api.get(`/api/squads/${discordSettingsSquad.id}`);
      setSquadDetails(prev => ({ ...prev, [discordSettingsSquad.id]: details }));
    } catch (err) { setError(err.message || 'Failed to save Discord settings'); }
    finally { setSavingDiscord(false); }
  };

  const handleUnlinkDiscord = async () => {
    if (!discordSettingsSquad || !confirm('Unlink Discord from this squad?')) return;
    setSavingDiscord(true);
    try {
      await api.delete(`/api/integrations/discord/squad/${discordSettingsSquad.id}/unlink`);
      setDiscordConfig({ server_id: '', channel_id: '', voice_category_id: '', voice_channel_id: '', sync_activity: false, sync_chat: false, widget_enabled: false });
      setSuccess('Discord unlinked!');
    } catch (err) { setError(err.message); }
    finally { setSavingDiscord(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(32px) saturate(180%)', WebkitBackdropFilter: 'blur(32px) saturate(180%)' }} onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }} style={{ background: `linear-gradient(165deg, ${THEME.bgCard} 0%, ${THEME.bgElevated} 100%)`, borderRadius: '24px', width: '95%', maxWidth: '500px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', border: `1px solid ${THEME.glassBorder}`, boxShadow: THEME.modalShadow }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '20px', borderBottom: `1px solid ${THEME.glassBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: `linear-gradient(180deg, ${THEME.bgElevated}80 0%, transparent 100%)` }}>
          <h3 style={{ margin: 0, color: THEME.text, fontSize: '18px', fontWeight: '700' }}>Squad Management</h3>
          <motion.button whileHover={{ scale: 1.1, backgroundColor: THEME.bgActive }} whileTap={{ scale: 0.95 }} onClick={onClose} style={{ background: THEME.bgHover, border: `1px solid ${THEME.glassBorder}`, color: THEME.textMuted, width: '36px', height: '36px', borderRadius: '10px', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&times;</motion.button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', padding: '12px 20px 0', borderBottom: `1px solid ${THEME.border}` }}>
          {[
            { id: 'my-squads', label: 'My Squads' },
            { id: 'create', label: '+ Create' },
            { id: 'join', label: '🔗 Join' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setError(null); setEditingSquad(null); }}
              style={{
                background: activeTab === tab.id ? myColor : 'transparent',
                color: activeTab === tab.id ? '#000' : THEME.textMuted,
                border: 'none', borderRadius: '8px 8px 0 0', padding: '10px 16px',
                fontWeight: '600', cursor: 'pointer', fontSize: '13px', marginBottom: '-1px',
                borderBottom: activeTab === tab.id ? `2px solid ${myColor}` : 'none'
              }}
            >{tab.label}</button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {error && <div style={{ background: `${THEME.danger}20`, border: `1px solid ${THEME.danger}40`, borderRadius: '10px', padding: '12px', marginBottom: '16px', color: THEME.danger, fontSize: '13px' }}>{error}</div>}
          {success && <div style={{ background: `${THEME.success}20`, border: `1px solid ${THEME.success}40`, borderRadius: '10px', padding: '12px', marginBottom: '16px', color: THEME.success, fontSize: '13px' }}>{success}</div>}

          {activeTab === 'my-squads' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {squads.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: THEME.textMuted }}>
                  <p style={{ marginBottom: '16px' }}>You're not in any squads yet.</p>
                  <button onClick={() => setActiveTab('create')} style={{ background: myColor, color: '#000', border: 'none', borderRadius: '10px', padding: '12px 24px', fontWeight: '600', cursor: 'pointer' }}>Create a Squad</button>
                </div>
              ) : (
                squads.map(squad => {
                  const details = squadDetails[squad.id];
                  const isOwner = details?.owner_id === currentUser?.id;
                  const isCurrent = currentSquad?.id === squad.id;

                  if (editingSquad?.id === squad.id) {
                    // Edit mode
                    return (
                      <div key={squad.id} style={{ background: THEME.bg, borderRadius: '12px', padding: '16px', border: `1px solid ${myColor}` }}>
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                          <select value={editIcon} onChange={e => setEditIcon(e.target.value)} style={{ background: THEME.bgHover, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '8px', fontSize: '20px', cursor: 'pointer' }}>
                            {SQUAD_ICONS.map(icon => <option key={icon} value={icon}>{icon}</option>)}
                          </select>
                          <input type="text" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Squad name" style={{ flex: 1, background: THEME.bgHover, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '10px', color: THEME.text, fontSize: '14px', outline: 'none' }} />
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={handleUpdateSquad} disabled={saving} style={{ flex: 1, background: THEME.success, color: '#000', border: 'none', borderRadius: '8px', padding: '10px', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer' }}>{saving ? 'Saving...' : 'Save'}</button>
                          <button onClick={() => setEditingSquad(null)} style={{ background: THEME.bgHover, color: THEME.textMuted, border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer' }}>Cancel</button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={squad.id} style={{ background: isCurrent ? `${myColor}15` : THEME.bg, borderRadius: '12px', padding: '16px', border: `1px solid ${isCurrent ? myColor : THEME.border}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '28px' }}>{squad.icon}</span>
                          <div>
                            <h4 style={{ margin: 0, color: THEME.text, fontSize: '16px', fontWeight: '600' }}>{squad.name}</h4>
                            <p style={{ margin: 0, color: THEME.textMuted, fontSize: '12px' }}>
                              {details?.members?.length || 0} member{(details?.members?.length || 0) !== 1 ? 's' : ''}
                              {isOwner && <span style={{ color: myColor, marginLeft: '8px' }}>👑 Owner</span>}
                            </p>
                          </div>
                        </div>
                        {isCurrent && <span style={{ background: myColor, color: '#000', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>Active</span>}
                      </div>

                      {/* Members */}
                      {details?.members && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                          {details.members.map(m => {
                            const memberKickVotes = kickVotes[squad.id]?.[m.id];
                            const isSelf = m.id === currentUser?.id;
                            const isFounder = m.is_founder && squad.is_founder_squad;
                            // Can kick if: 3+ members, not yourself, and (not a founder squad OR you're a founder OR target is not a founder)
                            const canKick = !isSelf && details.members.length > 2 && (!squad.is_founder_squad || currentUser?.is_founder || !m.is_founder);

                            return (
                              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: THEME.bg, borderRadius: '10px', border: `1px solid ${THEME.border}` }}>
                                <Avatar src={m.avatar_url} name={m.display_name} size={32} color={m.theme_color || THEME.success} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <span style={{ color: m.theme_color || THEME.text, fontWeight: '500', fontSize: '13px' }}>
                                    {m.founder_icon || ''} {m.display_name}
                                    {isSelf && <span style={{ color: THEME.textMuted, fontWeight: '400', marginLeft: '6px' }}>(you)</span>}
                                  </span>
                                </div>
                                {canKick && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    {memberKickVotes?.votes?.length > 0 && (
                                      <span style={{ color: THEME.danger, fontSize: '11px', fontWeight: '600' }}>
                                        {memberKickVotes.votes.length}/2 votes
                                      </span>
                                    )}
                                    {memberKickVotes?.voted ? (
                                      <button
                                        onClick={() => handleRemoveKickVote(squad.id, m.id)}
                                        disabled={kickingUser === m.id}
                                        style={{ background: THEME.danger, color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 8px', fontSize: '10px', fontWeight: '600', cursor: kickingUser === m.id ? 'not-allowed' : 'pointer' }}
                                        title="Remove your vote"
                                      >
                                        {kickingUser === m.id ? '...' : 'Voted'}
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => handleVoteToKick(squad.id, m.id)}
                                        disabled={kickingUser === m.id}
                                        style={{ background: THEME.bgHover, color: THEME.danger, border: `1px solid ${THEME.danger}40`, borderRadius: '6px', padding: '4px 8px', fontSize: '10px', fontWeight: '600', cursor: kickingUser === m.id ? 'not-allowed' : 'pointer' }}
                                        title="Vote to kick this member"
                                      >
                                        {kickingUser === m.id ? '...' : 'Kick'}
                                      </button>
                                    )}
                                  </div>
                                )}
                                {isFounder && <Badge color={THEME.omega} bg={`${THEME.omega}20`}>Founder</Badge>}
                              </div>
                            );
                          })}
                          {details.members.length > 2 && (
                            <p style={{ color: THEME.textDim, fontSize: '11px', margin: '4px 0 0', fontStyle: 'italic' }}>
                              {currentUser?.is_founder ? 'Founders can kick instantly' : '2 votes required to kick a member'}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {!isCurrent && (
                          <button onClick={() => onSquadSelect(squad)} style={{ background: myColor, color: '#000', border: 'none', borderRadius: '8px', padding: '8px 14px', fontWeight: '600', cursor: 'pointer', fontSize: '12px' }}>Switch to</button>
                        )}
                        {isOwner && (
                          <>
                            <button onClick={() => { setEditingSquad(squad); setEditName(squad.name); setEditIcon(squad.icon); }} style={{ background: THEME.bgHover, color: THEME.text, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
                            <button onClick={() => openDiscordSettings(squad)} style={{ background: THEME.bgHover, color: '#5865F2', border: `1px solid #5865F240`, borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '12px' }}>🎮 Discord</button>
                            <button onClick={() => copyInviteCode(details?.invite_code || squad.invite_code)} style={{ background: THEME.bgHover, color: THEME.info, border: `1px solid ${THEME.info}40`, borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '12px' }}>📋 Invite</button>
                          </>
                        )}
                        {/* Disband button for owner of small squads (2 members) */}
                        {isOwner && !squad.is_founder_squad && details?.members?.length === 2 && (
                          <button onClick={() => handleDisbandSquad(squad)} disabled={loading} style={{ background: THEME.danger, color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 14px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '600' }}>Disband Squad</button>
                        )}
                        {!isOwner && squads.length > 1 && (
                          <button onClick={() => handleLeaveSquad(squad)} disabled={loading} style={{ background: THEME.bgHover, color: THEME.danger, border: `1px solid ${THEME.danger}40`, borderRadius: '8px', padding: '8px 14px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '12px' }}>Leave</button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === 'create' && (
            <div>
              <p style={{ color: THEME.textMuted, fontSize: '14px', marginBottom: '20px' }}>Create a new squad to play with friends. You'll be the owner and can invite others.</p>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: myColor, fontSize: '12px', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase' }}>Squad Icon</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {SQUAD_ICONS.map(icon => (
                    <button key={icon} onClick={() => setNewSquadIcon(icon)} style={{
                      background: newSquadIcon === icon ? myColor : THEME.bg,
                      border: `1px solid ${newSquadIcon === icon ? myColor : THEME.border}`,
                      borderRadius: '10px', padding: '10px', fontSize: '20px', cursor: 'pointer', width: '44px', height: '44px'
                    }}>{icon}</button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: myColor, fontSize: '12px', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase' }}>Squad Name</label>
                <input type="text" value={newSquadName} onChange={e => setNewSquadName(e.target.value)} placeholder="e.g., The Gamers" maxLength={50} style={{ width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '14px', color: THEME.text, fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
              </div>

              <button onClick={handleCreateSquad} disabled={creating || !newSquadName.trim()} style={{
                width: '100%', background: myColor, color: '#000', border: 'none', borderRadius: '10px',
                padding: '14px', fontWeight: '600', cursor: (creating || !newSquadName.trim()) ? 'not-allowed' : 'pointer',
                fontSize: '15px', opacity: (creating || !newSquadName.trim()) ? 0.5 : 1
              }}>{creating ? 'Creating...' : `Create ${newSquadIcon} ${newSquadName || 'Squad'}`}</button>
            </div>
          )}

          {activeTab === 'join' && (
            <div>
              <p style={{ color: THEME.textMuted, fontSize: '14px', marginBottom: '20px' }}>Enter an invite code to join an existing squad. Ask the squad owner for the code.</p>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: myColor, fontSize: '12px', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase' }}>Invite Code</label>
                <input type="text" value={inviteCode} onChange={e => setInviteCode(e.target.value.toUpperCase())} placeholder="e.g., ABC123" maxLength={20} style={{ width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '14px', color: THEME.text, fontSize: '16px', fontFamily: 'monospace', letterSpacing: '2px', textAlign: 'center', outline: 'none', boxSizing: 'border-box' }} />
              </div>

              <button onClick={handleJoinSquad} disabled={joining || !inviteCode.trim()} style={{
                width: '100%', background: myColor, color: '#000', border: 'none', borderRadius: '10px',
                padding: '14px', fontWeight: '600', cursor: (joining || !inviteCode.trim()) ? 'not-allowed' : 'pointer',
                fontSize: '15px', opacity: (joining || !inviteCode.trim()) ? 0.5 : 1
              }}>{joining ? 'Joining...' : 'Join Squad'}</button>
            </div>
          )}

          {/* Discord Settings Modal Overlay */}
          {discordSettingsSquad && (
            <div style={{ position: 'absolute', inset: 0, background: THEME.bgCard, borderRadius: '20px', padding: '20px', overflowY: 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <button onClick={() => setDiscordSettingsSquad(null)} style={{ background: THEME.bgHover, border: 'none', color: THEME.textMuted, width: '32px', height: '32px', borderRadius: '8px', fontSize: '16px', cursor: 'pointer' }}>←</button>
                <div>
                  <h4 style={{ margin: 0, color: THEME.text, fontSize: '16px' }}>Discord Integration</h4>
                  <p style={{ margin: 0, color: THEME.textMuted, fontSize: '12px' }}>{discordSettingsSquad.name}</p>
                </div>
              </div>

              {loadingDiscord ? (
                <div style={{ textAlign: 'center', padding: '40px', color: THEME.textMuted }}>Loading Discord settings...</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Server Selection */}
                  <div>
                    <label style={{ display: 'block', color: '#5865F2', fontSize: '11px', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase' }}>Discord Server</label>
                    <select
                      value={discordConfig.server_id}
                      onChange={e => handleSelectDiscordServer(e.target.value)}
                      style={{ width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '12px', color: THEME.text, fontSize: '14px' }}
                    >
                      <option value="">Select a server...</option>
                      {discordGuilds.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                    {discordGuilds.length === 0 && !loadingDiscord && (
                      <p style={{ color: THEME.textMuted, fontSize: '11px', marginTop: '8px' }}>
                        No servers found. You need admin permissions on a Discord server.
                      </p>
                    )}
                  </div>

                  {/* Bot Not In Server Warning */}
                  {botNeedsInvite && discordConfig.server_id && (
                    <div style={{ background: '#5865F220', border: '1px solid #5865F2', borderRadius: '10px', padding: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                        <span style={{ fontSize: '24px' }}>🤖</span>
                        <div>
                          <h4 style={{ margin: 0, color: THEME.text, fontSize: '14px' }}>Bot Not in Server</h4>
                          <p style={{ margin: '4px 0 0', color: THEME.textMuted, fontSize: '12px' }}>
                            Add the Co-op Compendium bot to your server to enable channels and voice chat.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleInviteBot}
                        style={{ width: '100%', background: '#5865F2', color: '#fff', border: 'none', borderRadius: '8px', padding: '12px', fontWeight: '600', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                      >
                        <span>🔗</span> Add Bot to Server
                      </button>
                      <p style={{ color: THEME.textMuted, fontSize: '10px', marginTop: '10px', textAlign: 'center' }}>
                        After adding the bot, come back and re-select this server to load channels.
                      </p>
                    </div>
                  )}

                  {discordConfig.server_id && !botNeedsInvite && (
                    <>
                      {/* Activity Channel */}
                      <div>
                        <label style={{ display: 'block', color: '#5865F2', fontSize: '11px', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase' }}>Activity Channel (for status updates)</label>
                        <select
                          value={discordConfig.channel_id}
                          onChange={e => setDiscordConfig(prev => ({ ...prev, channel_id: e.target.value }))}
                          style={{ width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '12px', color: THEME.text, fontSize: '14px' }}
                        >
                          <option value="">None (no activity syncing)</option>
                          {(discordChannels.text_channels || []).map(c => (
                            <option key={c.id} value={c.id}>#{c.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Voice Channel Settings */}
                      <div style={{ background: THEME.bg, borderRadius: '10px', padding: '14px' }}>
                        <label style={{ display: 'block', color: '#5865F2', fontSize: '11px', marginBottom: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Voice Chat</label>

                        {/* Static Voice Channel */}
                        <div style={{ marginBottom: '12px' }}>
                          <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '6px' }}>Use Existing Channel</label>
                          <select
                            value={discordConfig.voice_channel_id}
                            onChange={e => setDiscordConfig(prev => ({ ...prev, voice_channel_id: e.target.value }))}
                            style={{ width: '100%', background: THEME.bgCard, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '10px', color: THEME.text, fontSize: '14px' }}
                          >
                            <option value="">Create temp channels</option>
                            {(discordChannels.voice_channels || []).map(c => (
                              <option key={c.id} value={c.id}>🔊 {c.name}</option>
                            ))}
                          </select>
                          <p style={{ color: THEME.textMuted, fontSize: '11px', marginTop: '4px' }}>
                            {discordConfig.voice_channel_id
                              ? 'Voice button opens this channel directly'
                              : 'Voice button creates temporary channels for each session'}
                          </p>
                        </div>

                        {/* Category for Temp Channels - only show if not using static */}
                        {!discordConfig.voice_channel_id && (
                          <div>
                            <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '6px' }}>Temp Channel Category</label>
                            <select
                              value={discordConfig.voice_category_id}
                              onChange={e => setDiscordConfig(prev => ({ ...prev, voice_category_id: e.target.value }))}
                              style={{ width: '100%', background: THEME.bgCard, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '10px', color: THEME.text, fontSize: '14px' }}
                            >
                              <option value="">Default category</option>
                              {(discordChannels.categories || []).map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                            </select>
                            <p style={{ color: THEME.textMuted, fontSize: '11px', marginTop: '4px' }}>Where temp voice channels are created</p>
                          </div>
                        )}
                      </div>

                      {/* Sync Options */}
                      <div style={{ background: THEME.bg, borderRadius: '10px', padding: '14px' }}>
                        <label style={{ display: 'block', color: '#5865F2', fontSize: '11px', marginBottom: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Sync Options</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={discordConfig.sync_activity} onChange={e => setDiscordConfig(prev => ({ ...prev, sync_activity: e.target.checked }))} style={{ width: '18px', height: '18px', accentColor: '#5865F2' }} />
                            <div>
                              <span style={{ color: THEME.text, fontSize: '13px', fontWeight: '500' }}>Sync Activity Feed</span>
                              <p style={{ margin: '2px 0 0', color: THEME.textMuted, fontSize: '11px' }}>Post status changes and ratings to Discord</p>
                            </div>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={discordConfig.sync_chat} onChange={e => setDiscordConfig(prev => ({ ...prev, sync_chat: e.target.checked }))} style={{ width: '18px', height: '18px', accentColor: '#5865F2' }} />
                            <div>
                              <span style={{ color: THEME.text, fontSize: '13px', fontWeight: '500' }}>Sync Squad Chat</span>
                              <p style={{ margin: '2px 0 0', color: THEME.textMuted, fontSize: '11px' }}>Messages in app appear in Discord</p>
                            </div>
                          </label>
                        </div>
                      </div>

                      {/* Save Buttons */}
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          onClick={handleSaveDiscordSettings}
                          disabled={savingDiscord}
                          style={{ flex: 1, background: '#5865F2', color: '#fff', border: 'none', borderRadius: '8px', padding: '12px', fontWeight: '600', cursor: savingDiscord ? 'not-allowed' : 'pointer' }}
                        >
                          {savingDiscord ? 'Saving...' : 'Save Settings'}
                        </button>
                        {discordConfig.server_id && (
                          <button
                            onClick={handleUnlinkDiscord}
                            disabled={savingDiscord}
                            style={{ background: THEME.bgHover, color: THEME.danger, border: `1px solid ${THEME.danger}40`, borderRadius: '8px', padding: '12px 16px', cursor: savingDiscord ? 'not-allowed' : 'pointer', fontSize: '12px' }}
                          >
                            Unlink
                          </button>
                        )}
                      </div>
                    </>
                  )}

                  {!discordConfig.server_id && discordGuilds.length > 0 && (
                    <p style={{ color: THEME.textMuted, fontSize: '13px', textAlign: 'center', padding: '20px' }}>
                      Select a Discord server to enable integration features.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default SquadManagementModal;
